import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { KlientLogoBgPicker } from '../klienti/KlientLogoBgPicker';
import { composeLogoOnBackground, logoFrameAttrs, logoFrameStyle, readFileAsDataUrl } from '../klienti/logo-compose';
import { useFeather } from '../shell/useFeather';
import { useTabParam } from '../shell/useTabParam';
import { CompactSelect } from '../components/arc/compact-select/compact-select';
import { Button } from '../components/arc/button/button';
import { Dialog, DialogContent } from '../components/arc/dialog/dialog';
import { PageState } from '../components/arc/page-state/page-state';
import { ArcPage, ArcPageHeader, ArcPanel, ArcTableFrame, ArcTabs } from '../components/arc/primitives/arc-primitives';
import { Select } from '../components/arc/select/select';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import { ServiceBar } from '../shell/ServiceBar';
import {
  getAffilmasterKlienti,
  getAffilmasterKlientDetail,
  getAffilmasterKlientCampaignChart,
  getAffilmasterKlientMonthAffiliates,
  getAffilmasterKlientAffiliates,
  createAffilmasterPartner,
  updateAffilmasterPartner,
  uploadAffilmasterLogo,
  getAffilmasterKlientContacts,
  getAffilmasterKlientInvoices,
  affilmasterKlientInvoicePdfUrl,
  getAffilmasterKlientNotes,
  getAffilmasterKlientDocuments,
  refreshAffilmasterKlientDocuments,
  setAffilmasterKlientDriveFolder,
  getAffilmasterPapCampaigns,
  pairAffilmasterCampaign,
  unpairAffilmasterCampaign,
  type PapCampaignPick,
  addAffilmasterKlientNote,
  updateAffilmasterKlientNote,
  deleteAffilmasterKlientNote,
  addAffilmasterKlientContact,
  updateAffilmasterKlientContact,
  deleteAffilmasterKlientContact,
  getAffilmasterKlientDeals,
  addAffilmasterKlientDeal,
  updateAffilmasterKlientDeal,
  setAffilmasterKlientDealStatus,
  uolCheckAffilmasterKlient,
  type KlientContact,
  type KlientDeal,
  type KlientDealKind,
  type KlientDealInvoiceGroup,
  type KlientDealStatus,
  type KlientInvoice,
  AffilmasterApiError,
  type KlientAffiliateRow,
  type KlientMonthData,
  type KlientListItem,
  type KlientDetail,
  type KlientMonth,
  setAffilmasterKlientVipBilling,
} from '../api/affilmaster';
import { DetailPerformanceChart, VipBadge, WebCell, WebStacked } from './AffilmasterAffiliatesPage';
import { CampaignChartModal } from './AffiliateDrilldownModals';
import { NgrRoiCard } from './NgrRoiCard';
import { mesicLabel, fmtDen, fmtCastka } from './affilmaster-format';
import { KlientDokumenty } from '../klienti/KlientDokumenty';
import { getCrmPlatformSuggestions } from '../api/crm';
import { KlientPromoNews } from './stranky/KlientPromoNews';
import { KlientVystupy } from './stranky/KlientVystupy';
import './affilmaster-detail.css';
import '../klienti/klienti.css';
import './affilmaster-client-list.css';
import { AffilmasterContactDirectory, type AffilmasterContactDirectoryItem } from './AffilmasterContactDirectory';
import { MailingGroupsField, saveMailingGroups, type MailingGroupsValue } from '../mailing/MailingGroupsField';
import { HelpdeskContactTickets } from '../helpdesk/HelpdeskContactTickets';
import { KlientSmlouvySection } from '../esign/KlientSmlouvy';
import { confirmDialog } from '../shell/dialogs';
import { AffilmasterNotesSection } from './AffilmasterNotesSection';
import { AffilmasterDetailMetricSection } from './AffilmasterDetailMetricSection';
import { AffilmasterDetailField, AffilmasterDetailFieldGroup, AffilmasterDetailSectionHeader } from './AffilmasterDetailFields';

const KLIENT_VIP_VIEWS = ['vse', 'am', 'vip'] as const;
const KLIENT_VIP_LABEL: Record<'vse' | 'am' | 'vip', string> = { vse: 'Celkem', am: 'Affilmaster', vip: 'VIP' };
const LIMITS = ['25', '50', '100', '200', 'all'];

function webHost(url: string | null): string | null {
  if (!url) return null;
  try { return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, ''); }
  catch { return url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, ''); }
}

const MARKET_FLAG: Record<string, [string, string]> = {
  CZ: ['🇨🇿', 'Česko'],
  SK: ['🇸🇰', 'Slovensko'],
};
function MarketFlag({ market }: { market: string }) {
  const f = MARKET_FLAG[market];
  const ico = f ? f[0] : '🌐';
  const label = f ? f[1] : market === 'FOREIGN' ? 'Global' : market;
  return <span className="pill pill-flag" data-sort-value={market}><span className="pill-flag-ico">{ico}</span>{label}</span>;
}
const LOGO_STYLE = { width: 34, height: 34, objectFit: 'contain', borderRadius: 10, background: '#fff', padding: 3 } as const;
// Robustní logo: při selhání načtení (přechodný 404 při restartu dev serveru)
// NEzmizí natrvalo, ale spadne na iniciály, logo se pak vrátí při reloadu.
function KlientLogoMini({ logo, logoBg, name }: { logo: string | null; logoBg?: string | null; name: string }) {
  const [err, setErr] = useState(false);
  if (logo && !err) return <img src={logo} alt="" className="klient-logo-mini" style={{ ...LOGO_STYLE, ...logoFrameStyle(logoBg) }} onError={() => setErr(true)} />;
  return (
    <span className="klient-logo-mini" style={{ ...LOGO_STYLE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 11, color: '#6b7280', border: '1px solid #e5e7eb' }}>
      {kInitials(name)}
    </span>
  );
}

export function klientSlug(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

interface AffilmasterKlientiPageProps {
  detailSlug?: string;
  onOpenDetail: (slug: string) => void;
  onOpenAffiliate: (id: string) => void;
  onOpenEsign?: (documentId: number) => void;
  onCloseDetail: () => void;
}

export function AffilmasterKlientiPage({ detailSlug, onOpenDetail, onOpenAffiliate, onOpenEsign, onCloseDetail }: AffilmasterKlientiPageProps) {
  const [items, setItems] = useState<KlientListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState('all');
  const [query, setQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const selected = useMemo(
    () => (detailSlug && items ? items.find((k) => klientSlug(k.name) === detailSlug) ?? null : null),
    [detailSlug, items],
  );

  useEffect(() => {
    let cancelled = false;
    getAffilmasterKlienti()
      .then((data) => { if (!cancelled) { setItems(data); setError(null); } })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof AffilmasterApiError ? e.message : 'Načtení klientů selhalo'); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    return items.filter((k) => !q || `${k.name} ${k.company ?? ''} ${k.website ?? ''}`.toLowerCase().includes(q));
  }, [items, query]);
  const visible = useMemo(() => (limit === 'all' ? filtered : filtered.slice(0, Number(limit))), [filtered, limit]);
  type ClientRow = KlientListItem & Record<string, unknown>;
  const tableRows: ClientRow[] = visible.map((item) => ({ ...item }));
  const columns: DataColumn<ClientRow>[] = [
    { key: 'name', label: 'Firma', sortable: true, render: (_value, row) => (
      <a className="am-client-name-link" href={`${window.location.pathname.replace(/\/$/, '')}/${klientSlug(row.name)}`} onClick={(event) => { event.preventDefault(); onOpenDetail(klientSlug(row.name)); }}>
        <KlientLogoMini logo={row.logo} logoBg={row.logoBg} name={row.name} /><strong>{row.name}</strong>
      </a>
    ) },
    { key: 'company', label: 'Společnost', sortable: true, render: (_value, row) => row.company ?? '—' },
    { key: 'website', label: 'Web', render: (_value, row) => <WebCell url={row.website} /> },
    { key: 'market', label: 'Trh', sortable: true, render: (_value, row) => <MarketFlag market={row.market} /> },
    { key: 'isExclusive', label: 'Exkluzivní', sortable: true, render: (_value, row) => row.isExclusive ? <span className="pill pill-star">★ Exkl.</span> : '—' },
    { key: 'currency', label: 'Měna', sortable: true, render: (_value, row) => <span className={`badge ${row.currency === 'EUR' ? 'badge-sk' : 'badge-cz'}`}>{row.currency}</span> },
    { key: 'campaigns', label: 'Kampaně', sortable: true, numeric: true },
  ];

  useFeather([items, selected, limit, query]);

  if (selected) return <KlientDetailView row={selected} onBack={onCloseDetail} onOpenAffiliate={onOpenAffiliate} onOpenEsign={onOpenEsign} />;

  if (error) return <ArcPage className="am-clients-list"><ArcPageHeader eyebrow="Affilmaster" title="Klienti" description="Přehled klientů, jejich kampaní a firemních údajů" /><PageState title="Klienty se nepodařilo načíst" message={error} /><Button variant="secondary" size="sm" onClick={() => setReloadKey((key) => key + 1)}>Načíst znovu</Button></ArcPage>;
  if (!items) return <ArcPage className="am-clients-list"><PageState title="Načítám klienty" loading /></ArcPage>;

  return (
    <ArcPage className="am-clients-list">
      <ArcPageHeader eyebrow="Affilmaster" title="Klienti" description="Přehled klientů, jejich kampaní a firemních údajů" meta={<Button type="button" size="sm" onClick={() => setShowAdd(true)}>Přidat klienta</Button>} />

      {showAdd && <AddKlientModal onCancel={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); setReloadKey((k) => k + 1); }} />}

      <ServiceBar variant="seg" selects={[{ label: 'Limit', value: limit, onChange: setLimit, options: LIMITS.map((value) => ({ value, label: value === 'all' ? 'Vše' : value })) }]} search={query} onSearch={setQuery} searchPlaceholder="Hledat firmu, společnost nebo web…" right={<span className="am-clients-list-count" role="status" aria-live="polite">{visible.length === items.length ? `${items.length} klientů` : `${visible.length} z ${items.length} klientů`}</span>} />

      <ArcTableFrame label="Seznam klientů"><SortableDataTable rows={tableRows} columns={columns} rowKey="id" caption="Seznam klientů" emptyMessage="Žádní klienti neodpovídají hledání." /></ArcTableFrame>
    </ArcPage>
  );
}

function AddKlientModal({ onCancel, onSaved }: { onCancel: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ name: '', ic: '', vat: '', market: 'CZ', billingCurrency: 'CZK' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    if (form.name.trim().length < 2) { setError('Název klienta musí mít alespoň 2 znaky'); return; }
    setSaving(true); setError(null);
    try {
      await createAffilmasterPartner({ name: form.name.trim(), ic: form.ic.trim(), vat: form.vat.trim(), market: form.market, billingCurrency: form.billingCurrency });
      onSaved();
    } catch (e) { setError(e instanceof AffilmasterApiError ? e.message : 'Založení klienta selhalo'); }
    finally { setSaving(false); }
  };
  return <Dialog open onOpenChange={(open) => { if (!open && !saving) onCancel(); }}>
    <DialogContent title="Nový klient" description="Základní firemní a fakturační údaje" className="am-client-create-dialog p1-arc-scope">
      <form className="am-client-create-form" onSubmit={(event) => { event.preventDefault(); void save(); }}>
        {error && <p className="am-client-create-error" role="alert">{error}</p>}
        <div className="am-client-create-field am-client-create-full"><label htmlFor="am-client-create-name">Název klienta *</label><input id="am-client-create-name" autoFocus required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div className="am-client-create-field"><label htmlFor="am-client-create-ic">IČ</label><input id="am-client-create-ic" value={form.ic} onChange={(e) => setForm({ ...form, ic: e.target.value })} /></div>
        <div className="am-client-create-field"><label htmlFor="am-client-create-vat">DIČ</label><input id="am-client-create-vat" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} /></div>
        <Select label="Trh" value={form.market} onValueChange={(market) => setForm({ ...form, market })} options={[{ value: 'CZ', label: 'Česko' }, { value: 'SK', label: 'Slovensko' }, { value: 'FOREIGN', label: 'Global' }]} />
        <Select label="Měna" value={form.billingCurrency} onValueChange={(billingCurrency) => setForm({ ...form, billingCurrency })} options={[{ value: 'CZK', label: 'CZK' }, { value: 'EUR', label: 'EUR' }]} />
        <div className="am-client-create-actions"><Button type="button" variant="secondary" size="sm" disabled={saving} onClick={onCancel}>Zrušit</Button><Button type="submit" size="sm" loading={saving}>Vytvořit klienta</Button></div>
      </form>
    </DialogContent>
  </Dialog>;
}

const KLIENT_SECTION_HINT = 'p1-klient-section';

export function hintKlientSection(section: string): void {
  try { window.sessionStorage.setItem(KLIENT_SECTION_HINT, section); } catch { return; }
}

function peekKlientSectionHint(): string | null {
  try {
    const v = window.sessionStorage.getItem(KLIENT_SECTION_HINT);
    return v && KSECTIONS.some((s) => s.id === v) ? v : null;
  } catch { return null; }
}

function clearKlientSectionHint(): void {
  try { window.sessionStorage.removeItem(KLIENT_SECTION_HINT); } catch { return; }
}

const KSECTIONS = [
  { id: 'prehled', label: 'Přehledy' },
  { id: 'udaje', label: 'Údaje' },
  { id: 'dealy', label: 'Dealy' },
  { id: 'kontakty', label: 'Kontakty' },
  { id: 'poznamky', label: 'Poznámky' },
  { id: 'mesice', label: 'Měsíční rozpad' },
  { id: 'kampane', label: 'Kampaně' },
  { id: 'promo-news', label: 'Promo News' },
  { id: 'vystupy', label: 'Výstupy' },
  { id: 'affiliates', label: 'Affiliates' },
  { id: 'doklady', label: 'Doklady' },
  { id: 'smlouvy', label: 'Smlouvy' },
  { id: 'dokumenty', label: 'Dokumenty' },
  { id: 'helpdesk', label: 'Helpdesk' },
] as const;

const CZ_MON = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];
const CZ_MON_GEN = ['ledna', 'února', 'března', 'dubna', 'května', 'června', 'července', 'srpna', 'září', 'října', 'listopadu', 'prosince'];
function kLongDate(ymd: string | null): string | null {
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d || m < 1 || m > 12) return null;
  return `${d}. ${CZ_MON_GEN[m - 1]} ${y}`;
}
function kDateTime(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('cs-CZ', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date);
}
function kInitials(s: string): string { const p = s.trim().split(/\s+/).filter(Boolean); return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || '?'; }
function kMonthLabel(ym: string): string {
  const [y, m] = ym.split('-'); const i = Number(m) - 1;
  if (i < 0 || i >= 12) return ym;
  const name = CZ_MON[i] ?? ''; return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${y}`;
}
function kInt(n: number): string { return new Intl.NumberFormat('cs-CZ').format(Math.round(n || 0)); }
function kMoney(n: number, currency: string): string {
  const v = new Intl.NumberFormat('cs-CZ').format(Math.round(n || 0));
  return currency === 'EUR' ? `${v} €` : `${v} Kč`;
}
function kPct1(n: number): string { return new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n || 0); }
function roiColorHex(roi: number | null): string | undefined {
  if (roi == null) return undefined;
  return roi >= 100 ? '#34c759' : roi >= 0 ? '#ff9500' : '#c82b23';
}
function kRoi(ngr: number, comm: number, _net = true): string | null {
  if (!comm) return null;
  return new Intl.NumberFormat('cs-CZ').format(Math.round(((ngr - comm) / comm) * 100));
}
function kPeriod(first: string | null, last: string | null): string {
  if (!first || !last) return '-';
  const f = (d: string) => { const [y, m] = d.split('-'); return `${Number(m)}/${(y ?? '').slice(2)}`; };
  return `${f(first)}–${f(last)}`;
}
function RowArrow({ title, onClick }: { title: string; onClick: () => void }) {
  return (
    <button type="button" className="prototype-row-arrow-btn" title={title} onClick={onClick} aria-label={title}>
      <span className="feather-icon" aria-hidden="true"><i data-feather="chevron-right" /></span>
    </button>
  );
}
type KDrillModal = { kind: 'campaign'; cid: string; cname: string } | { kind: 'month'; ym: string } | null;

function KlientMonthModal({ id, ym, onClose }: { id: number; ym: string; onClose: () => void }) {
  const [data, setData] = useState<KlientMonthData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getAffilmasterKlientMonthAffiliates(id, ym)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Rozpad se nepodařilo načíst'); });
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { cancelled = true; window.removeEventListener('keydown', onKey); };
  }, [id, ym, onClose]);
  useFeather([data]);
  const cur = data?.currency ?? 'CZK';
  const t = data?.totals;
  const ftdRatio = t && t.registrations ? `${kPct1((t.ftd_count / t.registrations) * 100)} %` : '-';
  const roiNum = t && t.commission ? Math.round(((t.ngr_volume - t.commission) / t.commission) * 100) : null;
  const rsPct = t && t.ngr_volume ? Math.round((t.commission / t.ngr_volume) * 100) : null;
  const kpis = t ? [
    { l: 'Affiliates', v: kInt(t.affiliates) }, { l: 'Kampaně', v: kInt(t.campaigns) },
    { l: 'Registrace', v: kInt(t.registrations) }, { l: 'FTD', v: kInt(t.ftd_count) },
    { l: 'FTD Ratio', v: ftdRatio }, { l: 'Vklady', v: kMoney(t.deposits_volume, cur) },
    { l: 'NGR', v: kMoney(t.ngr_volume, cur) }, { l: 'Revshare %', v: rsPct != null ? `${kInt(rsPct)} %` : '-' },
    { l: 'Odm. RS', v: kMoney(t.commission_rs, cur) }, { l: 'Odm. CPA', v: kMoney(t.commission_cpa, cur) },
    { l: 'NGR ROI', v: roiNum != null ? `${kInt(roiNum)} %` : '-', color: roiColorHex(roiNum) },
  ] : [];
  return (
    <div className="aff-modal-backdrop" onClick={onClose}>
      <div className="aff-modal aff-modal-xl aff-modal-klient-month" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="aff-modal-head">
          <div className="min-w-0"><div className="aff-modal-title">Affiliates, {ym}</div><div className="aff-modal-sub">Rozpad klienta za měsíc</div></div>
          <button type="button" className="btn-close" aria-label="Zavřít" onClick={onClose} />
        </div>
        <div className="aff-modal-body">
          {err && <div className="alert alert-danger mb-0">{err}</div>}
          {!err && !data && <div className="text-muted"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>}
          {data && (
            <>
              <div className="kmonth-kpi-grid mb-3">
                {kpis.map((k) => (
                  <div className="kmonth-kpi" key={k.l}>
                    <div className="kmonth-kpi-label">{k.l}</div>
                    <div className="kmonth-kpi-value" style={k.color ? { color: k.color } : undefined}>{k.v}</div>
                  </div>
                ))}
              </div>
              <ArcTableFrame className="aff-table-wrap aff-month-table-frame" label="Affiliates v detailu měsíce" sticky="container" maxHeight="min(62dvh, 640px)">
                <table className="table table-sm align-middle mb-0 aff-detail-table aff-breakdown-table">
                  <thead><tr>
                    <th className="ps-2">Affiliate</th><th className="text-center">Kampaně</th><th className="text-end">Registrace</th><th className="text-end">FTD</th>
                    <th className="text-end">Vklady</th><th className="text-end">NGR</th><th className="text-end">Odm. RS</th><th className="text-end">Odm. CPA</th>
                    <th className="text-end">AM RS</th><th className="text-end">AM CPA</th><th className="text-end">NGR ROI</th>
                  </tr></thead>
                  <tbody>
                    {data.affiliates.length === 0 && <tr><td colSpan={11} className="text-center text-muted py-3">Žádní affiliati.</td></tr>}
                    {data.affiliates.map((a) => {
                      const r = kRoi(a.ngr_volume, a.commission, false);
                      const rNum = a.commission ? Math.round(((a.ngr_volume - a.commission) / a.commission) * 100) : null;
                      return (
                        <tr key={a.pap_affiliate_id}>
                          <td className="ps-2"><strong>{a.name}</strong><div className="fs-8 text-muted">{a.pap_affiliate_id}</div></td>
                          <td className="text-center">{kInt(a.campaigns)}</td>
                          <td className="text-end">{kInt(a.registrations)}</td>
                          <td className="text-end">{kInt(a.ftd_count)}</td>
                          <td className="text-end">{kMoney(a.deposits_volume, cur)}</td>
                          <td className="text-end">{kMoney(a.ngr_volume, cur)}</td>
                          <td className="text-end">{kMoney(a.commission_rs, cur)}</td>
                          <td className="text-end">{kMoney(a.commission_cpa, cur)}</td>
                          <td className="text-end text-muted">-</td>
                          <td className="text-end text-muted">-</td>
                          <td className="text-end" style={rNum != null ? { color: roiColorHex(rNum) } : undefined}>{r ? `${r} %` : <span className="text-muted">-</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ArcTableFrame>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

type ContactForm = { name: string; email: string; phone: string; position: string; vocative: string; invoiceCc?: boolean };
const EMPTY_CONTACT: ContactForm = { name: '', email: '', phone: '', position: '', vocative: '' };

const ROW_ONLY_SECTIONS = new Set<string>(['dealy', 'kontakty', 'poznamky', 'doklady', 'smlouvy', 'dokumenty', 'promo-news', 'vystupy', 'helpdesk']);

function KlientDokladySection({ id }: { id: number }) {
  const [rows, setRows] = useState<KlientInvoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rok, setRok] = useState<string>(String(new Date().getFullYear()));
  useEffect(() => {
    let cancelled = false;
    getAffilmasterKlientInvoices(id)
      .then((d) => { if (!cancelled) setRows(d); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof AffilmasterApiError ? e.message : 'Načtení dokladů selhalo'); });
    return () => { cancelled = true; };
  }, [id]);

  const roky = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows ?? []) {
      const y = (r.issuedDate ?? '').slice(0, 4);
      if (y) set.add(y);
    }
    return [...set].sort().reverse();
  }, [rows]);

  // Když klient v aktuálním roce doklady nemá, ukáže se nejbližší rok, který má.
  useEffect(() => {
    if (roky.length > 0 && !roky.includes(rok)) setRok(roky[0]!);
  }, [roky, rok]);

  useFeather([rows, rok]);

  const skupiny = useMemo(() => {
    const map = new Map<string, KlientInvoice[]>();
    for (const r of rows ?? []) {
      if ((r.issuedDate ?? '').slice(0, 4) !== rok) continue;
      const ym = (r.issuedDate ?? '').slice(0, 7) || 'bez-data';
      const list = map.get(ym) ?? [];
      list.push(r);
      map.set(ym, list);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [rows, rok]);

  if (error) return <div className="alert alert-danger mb-0">{error}</div>;
  if (!rows) return <div className="text-muted py-3"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>;
  if (rows.length === 0) return <div className="text-muted py-3">Pro tohoto klienta nejsou v UOL žádné doklady.</div>;

  return (
    <div className="klient-doklady">
      <AffilmasterDetailSectionHeader title="Doklady" description="Vystavené doklady klienta podle měsíce" />
      <div className="doklady-filtr">
        <span className="doklady-filtr-label">Rok</span>
        <ArcTabs items={roky.map((y) => ({ value: y, label: y }))} value={rok} onChange={setRok} label="Rok dokladů" />
      </div>
      {skupiny.length === 0 && <div className="text-muted py-3">V roce {rok} nejsou žádné doklady.</div>}
      {skupiny.map(([ym, list]) => {
        const soucet = list.reduce((n, r) => n + (r.totalAmount ?? 0), 0);
        const mena = list.find((r) => r.currency)?.currency ?? null;
        const neuhrazeno = list.filter((r) => !r.paid).length;
        return (
          <section className="doklady-mesic" key={ym}>
            <header className="doklady-mesic-head">
              <span className="doklady-mesic-nazev">{ym === 'bez-data' ? 'Bez data vystavení' : mesicLabel(ym)}</span>
              <span className="doklady-mesic-pocet">{list.length === 1 ? '1 doklad' : `${list.length} doklady`}</span>
              <span className="doklady-mesic-suma">{fmtCastka(soucet, mena)}</span>
              {neuhrazeno > 0 && <span className="doklady-mesic-dluh">{neuhrazeno}× neuhrazeno</span>}
            </header>
            <ol className="doklady-vetev">
              {list.map((r) => (
                <ArcPanel as="li" className="doklady-radek" key={r.invoiceNumber ?? `${ym}-${r.issuedDate}`}>
                  <span className="doklady-cislo">
                    {r.invoiceNumber ?? '—'}
                    {r.docType === 'corrective' && <em className="doklady-dobropis">dobropis</em>}
                  </span>
                  <span className="doklady-datum">{fmtDen(r.issuedDate)}</span>
                  <span className="doklady-splatnost">{fmtDen(r.dueDate)}</span>
                  <span className="doklady-castka">{fmtCastka(r.totalAmount, r.currency)}</span>
                  <span className={`doklady-stav${r.paid ? ' je-uhrazeno' : ''}`}>
                    {r.paid ? 'Uhrazeno' : 'Neuhrazeno'}
                  </span>
                  {r.invoiceNumber && (
                    <a
                      className="doklady-pdf"
                      href={affilmasterKlientInvoicePdfUrl(id, r.invoiceNumber)}
                      title={`Stáhnout doklad ${r.invoiceNumber} v PDF`}
                      aria-label={`Stáhnout doklad ${r.invoiceNumber} v PDF`}
                    >
                      <i data-feather="download" />
                    </a>
                  )}
                </ArcPanel>
              ))}
            </ol>
          </section>
        );
      })}
    </div>
  );
}

function KlientPoznamkySection({ id }: { id: number }) {
  return (
    <AffilmasterNotesSection
      resourceKey={`client:${id}`}
      loadNotes={() => getAffilmasterKlientNotes(id)}
      addNote={(body) => addAffilmasterKlientNote(id, body)}
      updateNote={(noteId, body) => updateAffilmasterKlientNote(id, noteId, body)}
      deleteNote={(noteId) => deleteAffilmasterKlientNote(id, noteId)}
      placeholder="Podmínky spolupráce, dohody nebo důležitý kontext…"
      emptyText="Důležité dohody a kontext klienta budou přehledně na jednom místě."
    />
  );
}

function KlientHelpdeskSection({ id }: { id: number }) {
  const [emails, setEmails] = useState<string[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    getAffilmasterKlientContacts(id)
      .then((rows) => { if (!cancelled) setEmails(rows.map((c) => c.email ?? '')); })
      .catch(() => { if (!cancelled) setEmails([]); });
    return () => { cancelled = true; };
  }, [id]);
  return (
    <HelpdeskContactTickets emails={emails} />
  );
}

const DEAL_KIND_LABEL: Record<KlientDealKind, string> = {
  fix: 'Fixní položka',
  affiliate_passthrough: 'Přefakturace provizí affiliatů',
  rs_fee: 'Provize z NGR',
  cpa_fee: 'Provize z CPA',
  reg_fee: 'Provize za uznanou registraci',
};
const DEAL_STATUS_LABEL: Record<KlientDealStatus, string> = { pripraveno: 'Připraveno', aktivni: 'Aktivní', ukonceny: 'Ukončený' };
type DealForm = { kind: KlientDealKind; title: string; amount: string; percent: string; currency: 'CZK' | 'EUR'; exchangeRate: string; invoiceGroup: KlientDealInvoiceGroup; uolProductId: string; validFrom: string; validTo: string; status: KlientDealStatus; note: string; excludedAffiliates: string; includeDeclined: boolean };
const DEAL_DEFAULT_GROUP: Record<KlientDealKind, KlientDealInvoiceGroup> = { fix: 'fixni', affiliate_passthrough: 'provize', rs_fee: 'provize', cpa_fee: 'provize', reg_fee: 'provize' };
const DEAL_DEFAULT_PRODUCT: Record<KlientDealKind, string> = { fix: 'affiliate_service', affiliate_passthrough: 'affiliate_revenue', rs_fee: 'affiliate_service', cpa_fee: 'affiliate_service', reg_fee: 'affiliate_service' };

function dealValue(dl: KlientDeal): string {
  if (dl.kind === 'fix') return dl.amount == null ? '-' : `${kMoney(dl.amount, dl.currency)} bez DPH / měsíc`;
  if (dl.kind === 'rs_fee') return dl.percent == null ? '-' : `${kPct1(dl.percent)} % z NGR`;
  if (dl.kind === 'cpa_fee') return dl.percent == null ? '-' : `${kPct1(dl.percent)} % z CPA provizí`;
  if (dl.kind === 'reg_fee') return dl.amount == null ? '-' : `${kMoney(dl.amount, dl.currency)} za uznanou registraci`;
  return dl.exchangeRate == null ? 'řádek per affiliate' : `řádek per affiliate, kurz ${dl.exchangeRate} CZK/EUR`;
}

function dealPeriod(dl: KlientDeal): string {
  const f = (v: string | null) => (v ? `${v.slice(8, 10)}.${v.slice(5, 7)}.${v.slice(0, 4)}` : null);
  const a = f(dl.validFrom); const b = f(dl.validTo);
  if (!a && !b) return 'účtovat od: nezadáno';
  if (a && b) return `${a} – ${b}`;
  return a ? `od ${a}` : `od: nezadáno, do ${b}`;
}

function dealMissingStart(dl: KlientDeal): boolean {
  return !dl.validFrom && dl.status !== 'ukonceny';
}

const dealsCache = new Map<number, Promise<KlientDeal[]>>();
function loadKlientDeals(id: number, fresh = false): Promise<KlientDeal[]> {
  if (fresh || !dealsCache.has(id)) {
    dealsCache.set(id, getAffilmasterKlientDeals(id).catch((e: unknown) => { dealsCache.delete(id); throw e; }));
  }
  return dealsCache.get(id) as Promise<KlientDeal[]>;
}

function KlientDealySection({ id, defaultCurrency, vipBilling, vipInstances, onVipBillingChanged }: { id: number; defaultCurrency: 'CZK' | 'EUR'; vipBilling: boolean; vipInstances: Array<{ code: string; casino: string; accounts: number }>; onVipBillingChanged: () => void }) {
  const { me } = useAuth();
  const canManage = !!me?.permissionKeys?.includes('affilmaster:manage');
  const [vipBusy, setVipBusy] = useState(false);
  const [vipErr, setVipErr] = useState<string | null>(null);
  async function toggleVip(enabled: boolean) {
    setVipBusy(true); setVipErr(null);
    try { await setAffilmasterKlientVipBilling(id, enabled); onVipBillingChanged(); }
    catch (e) { setVipErr(e instanceof AffilmasterApiError ? e.message : 'Změna se neuložila'); }
    finally { setVipBusy(false); }
  }
  const [rows, setRows] = useState<KlientDeal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [form, setForm] = useState<DealForm | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    loadKlientDeals(id, reload > 0)
      .then((d) => { if (!cancelled) setRows(d); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof AffilmasterApiError ? e.message : 'Načtení dealů selhalo'); });
    return () => { cancelled = true; };
  }, [id, reload]);
  useFeather([rows, form]);
  const emptyForm = (): DealForm => ({ kind: 'fix', title: '', amount: '', percent: '', currency: defaultCurrency, exchangeRate: '', invoiceGroup: 'fixni', uolProductId: DEAL_DEFAULT_PRODUCT.fix, validFrom: '', validTo: '', status: 'aktivni', note: '', excludedAffiliates: '', includeDeclined: false });
  function openAdd() { setEditId(null); setFormErr(null); setForm(emptyForm()); }
  function openEdit(dl: KlientDeal) {
    setEditId(dl.id); setFormErr(null);
    setForm({ kind: dl.kind, title: dl.title, amount: dl.amount == null ? '' : String(dl.amount), percent: dl.percent == null ? '' : String(dl.percent), currency: dl.currency === 'EUR' ? 'EUR' : 'CZK', exchangeRate: dl.exchangeRate == null ? '' : String(dl.exchangeRate), invoiceGroup: dl.invoiceGroup, uolProductId: dl.uolProductId ?? '', validFrom: dl.validFrom ?? '', validTo: dl.validTo ?? '', status: dl.status, note: dl.note ?? '', excludedAffiliates: (dl.excludedAffiliates ?? []).join(', '), includeDeclined: !!dl.includeDeclined });
  }
  function changeKind(kind: KlientDealKind) {
    if (!form) return;
    const invoiceGroup = DEAL_DEFAULT_GROUP[kind];
    setForm({ ...form, kind, invoiceGroup, uolProductId: form.uolProductId && form.uolProductId !== DEAL_DEFAULT_PRODUCT[form.kind] ? form.uolProductId : DEAL_DEFAULT_PRODUCT[kind] });
  }
  async function save() {
    if (!form) return;
    if (form.title.trim().length < 2) { setFormErr('Název musí mít aspoň 2 znaky'); return; }
    if ((form.kind === 'fix' || form.kind === 'reg_fee') && !form.amount.trim()) { setFormErr('Zadej částku'); return; }
    if ((form.kind === 'rs_fee' || form.kind === 'cpa_fee') && !form.percent.trim()) { setFormErr('Zadej sazbu v %'); return; }
    setBusy(true); setFormErr(null);
    try {
      const p = { kind: form.kind, title: form.title.trim(), amount: form.amount.trim() || null, percent: form.percent.trim() || null, currency: form.currency, exchangeRate: form.exchangeRate.trim() || null, invoiceGroup: form.invoiceGroup, uolProductId: form.uolProductId.trim() || null, validFrom: form.validFrom || null, validTo: form.validTo || null, status: form.status, note: form.note.trim() || null, excludedAffiliates: form.excludedAffiliates.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean), includeDeclined: form.includeDeclined };
      if (editId == null) await addAffilmasterKlientDeal(id, p);
      else await updateAffilmasterKlientDeal(id, editId, p);
      setForm(null); setReload((r) => r + 1);
    } catch (e) { setFormErr(e instanceof AffilmasterApiError ? e.message : 'Uložení selhalo'); }
    finally { setBusy(false); }
  }
  async function changeStatus(dl: KlientDeal, status: KlientDealStatus) {
    if (status === 'ukonceny' && !(await confirmDialog({ title: 'Ukončit deal?', message: `Deal „${dl.title}“ přejde do stavu Ukončený.`, confirmLabel: 'Ukončit', danger: true }))) return;
    if (status === 'aktivni' && !dl.validFrom) {
      openEdit(dl);
      setForm((f) => (f ? { ...f, status: 'aktivni' } : f));
      setFormErr('Doplň „Účtovat od“, bez něj nejde deal aktivovat.');
      return;
    }
    try { await setAffilmasterKlientDealStatus(id, dl.id, status); setReload((r) => r + 1); }
    catch (e) { setError(e instanceof AffilmasterApiError ? e.message : 'Změna stavu selhala'); }
  }
  if (error) return <div className="alert alert-danger mb-0">{error}</div>;
  if (!rows) return <div className="text-muted py-3"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>;
  const showPercent = form?.kind === 'rs_fee' || form?.kind === 'cpa_fee';
  const showAmount = form?.kind === 'fix' || form?.kind === 'reg_fee';
  const showRate = form?.kind !== 'fix' && form?.kind !== 'reg_fee';
  return (
    <section>
      <AffilmasterDetailSectionHeader title="Dealy" description={`${rows.length} ${rows.length === 1 ? 'deal' : rows.length < 5 ? 'dealy' : 'dealů'} klienta`} action={canManage && (
        <Button type="button" size="sm" onClick={openAdd}><i data-feather="plus" style={{ width: 14, height: 14 }} /> Přidat deal</Button>
      )} />
      {vipInstances.length > 0 && (
        <ArcPanel as="div" className="dealy-vip-toggle">
          <label className="form-check">
            <input className="form-check-input" type="checkbox" checked={vipBilling} disabled={!canManage || vipBusy} onChange={(e) => void toggleVip(e.target.checked)} />
            <span className="form-check-label"><strong>Účtovat i VIP</strong> <VipBadge /></span>
          </label>
          <small className="text-muted">
            Výkon VIP affiliate partnerů z vlastní PaP instance kasina ({vipInstances.map((i) => `${i.casino}: ${i.accounts} ${i.accounts === 1 ? 'účet' : i.accounts < 5 ? 'účty' : 'účtů'}`).join(', ')}) se započítá do statistik a vyúčtování klienta.
          </small>
          {vipErr && <div className="text-danger"><small>{vipErr}</small></div>}
        </ArcPanel>
      )}
      {rows.length === 0 ? <div className="prototype-empty-contact">Zatím žádné dealy.</div> : (
        <div className="dealy-vypis">
          {(['fixni', 'provize'] as KlientDealInvoiceGroup[]).map((group) => {
            const list = rows.filter((r) => r.invoiceGroup === group);
            if (list.length === 0) return null;
            const fixSum = list.filter((r) => r.status === 'aktivni' && r.kind === 'fix' && r.amount != null).reduce((a, r) => a + (r.amount ?? 0), 0);
            const fixCurrency = list.find((r) => r.kind === 'fix')?.currency ?? defaultCurrency;
            return (
              <section className="dealy-skupina" key={group}>
                <header className="dealy-skupina-head">
                  <span className="dealy-skupina-nazev">{group === 'fixni' ? 'Faktura 1 · Fixní' : 'Faktura 2 · Provize'}</span>
                  <span className="dealy-skupina-pocet">{list.length === 1 ? '1 deal' : `${list.length} dealy`}</span>
                  {group === 'fixni' && fixSum > 0 && <span className="dealy-skupina-suma">{kMoney(fixSum, fixCurrency)} bez DPH / měsíc</span>}
                </header>
                <ol className="dealy-vetev">
                  {list.map((dl) => (
                    <ArcPanel as="li" className={`dealy-radek${dl.status === 'ukonceny' ? ' je-ukonceny' : ''}`} key={dl.id}>
                      <span className="dealy-nazev">
                        <em className="dealy-typ">{DEAL_KIND_LABEL[dl.kind]}</em>
                        {dl.title}
                        {dl.note && <small className="dealy-poznamka">{dl.note}</small>}
                        {dl.excludedAffiliates?.length > 0 && <small className="dealy-poznamka">bez affiliate {dl.excludedAffiliates.join(', ')}</small>}
                        {dl.includeDeclined && <small className="dealy-poznamka">Approved + Declined</small>}
                      </span>
                      <span className="dealy-hodnota">{dealValue(dl)}</span>
                      <span className="dealy-produkt">{dl.uolProductId ?? '-'}</span>
                      <span className={`dealy-platnost${dealMissingStart(dl) ? ' je-bez-data' : ''}`} title={dealMissingStart(dl) ? 'Deal nemá nastavené Účtovat od' : undefined}>{dealPeriod(dl)}</span>
                      <span className={`dealy-stav je-${dl.status}`}>{DEAL_STATUS_LABEL[dl.status]}</span>
                      {canManage ? (
                        <span className="dealy-akce">
                          <button type="button" onClick={() => openEdit(dl)} title={`Upravit ${dl.title}`} aria-label={`Upravit ${dl.title}`}><i data-feather="edit-2" /></button>
                          {dl.status === 'pripraveno' && <button type="button" onClick={() => void changeStatus(dl, 'aktivni')} title={`Aktivovat ${dl.title}`} aria-label={`Aktivovat ${dl.title}`}><i data-feather="play-circle" /></button>}
                          {dl.status === 'aktivni' && <button type="button" className="is-danger" onClick={() => void changeStatus(dl, 'ukonceny')} title={`Ukončit ${dl.title}`} aria-label={`Ukončit ${dl.title}`}><i data-feather="x-circle" /></button>}
                          {dl.status === 'ukonceny' && <button type="button" onClick={() => void changeStatus(dl, 'aktivni')} title={`Obnovit ${dl.title}`} aria-label={`Obnovit ${dl.title}`}><i data-feather="rotate-ccw" /></button>}
                        </span>
                      ) : <span />}
                    </ArcPanel>
                  ))}
                </ol>
              </section>
            );
          })}
        </div>
      )}
      {form && (
        <div className="prototype-modal-backdrop" onClick={() => setForm(null)}>
          <div onClick={(e) => e.stopPropagation()} className="card prototype-modal-card" style={{ width: '100%', maxWidth: 560 }}>
            <div className="prototype-modal-head"><div><div className="prototype-eyebrow">DEAL</div><h2>{editId == null ? 'Přidat deal' : 'Upravit deal'}</h2></div><button type="button" className="prototype-modal-close" aria-label="Zavřít" onClick={() => setForm(null)}>×</button></div>
            <div className="card-body">
              {formErr && <div className="alert alert-danger py-2">{formErr}</div>}
              <div className="row g-2">
                <div className="col-md-6"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Typ dealu *</label>
                  <select className="form-select form-select-sm" value={form.kind} onChange={(e) => changeKind(e.target.value as KlientDealKind)}>
                    {(Object.keys(DEAL_KIND_LABEL) as KlientDealKind[]).map((k) => <option key={k} value={k}>{DEAL_KIND_LABEL[k]}</option>)}
                  </select></div>
                <div className="col-md-6"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Faktura *</label>
                  <select className="form-select form-select-sm" value={form.invoiceGroup} onChange={(e) => setForm({ ...form, invoiceGroup: e.target.value as KlientDealInvoiceGroup })}>
                    <option value="fixni">1 – Fixní</option><option value="provize">2 – Provize</option>
                  </select></div>
                <div className="col-12"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Název *</label>
                  <input className="form-control form-control-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={form.kind === 'fix' ? 'např. VIP instance' : form.kind === 'rs_fee' ? 'např. Provize z NGR' : form.kind === 'cpa_fee' ? 'např. Provize z CPA' : form.kind === 'reg_fee' ? 'např. Provize za registraci' : 'např. Affiliate Commission'} /></div>
                {showAmount && <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>{form.kind === 'reg_fee' ? 'Částka za registraci *' : 'Částka bez DPH *'}</label>
                  <input className="form-control form-control-sm" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>}
                {showPercent && <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Sazba % *</label>
                  <input className="form-control form-control-sm" inputMode="decimal" value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} /></div>}
                <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Měna</label>
                  <select className="form-select form-select-sm" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value as 'CZK' | 'EUR' })}><option>CZK</option><option>EUR</option></select></div>
                {showRate && <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Kurz CZK/EUR</label>
                  <input className="form-control form-control-sm" inputMode="decimal" value={form.exchangeRate} onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })} placeholder="např. 25" /></div>}
                <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>UOL produkt</label>
                  <input className="form-control form-control-sm" value={form.uolProductId} onChange={(e) => setForm({ ...form, uolProductId: e.target.value })} /></div>
                <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Účtovat od</label>
                  <input type="date" className="form-control form-control-sm" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} /></div>
                <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Účtovat do</label>
                  <input type="date" className="form-control form-control-sm" value={form.validTo} onChange={(e) => setForm({ ...form, validTo: e.target.value })} /></div>
                <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Stav</label>
                  <select className="form-select form-select-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as KlientDealStatus })}>
                    {(Object.keys(DEAL_STATUS_LABEL) as KlientDealStatus[]).map((k) => <option key={k} value={k}>{DEAL_STATUS_LABEL[k]}</option>)}
                  </select></div>
                <div className="col-md-8"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Poznámka</label>
                  <input className="form-control form-control-sm" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
                {showPercent && <div className="col-12"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Vyloučení affiliate partneři (REFID z PaP, oddělené čárkou)</label>
                  <input className="form-control form-control-sm" value={form.excludedAffiliates} onChange={(e) => setForm({ ...form, excludedAffiliates: e.target.value })} placeholder="např. 8FjOf" /></div>}
                {showPercent && <div className="col-12"><label className="form-check" style={{ fontSize: '0.82rem' }}><input className="form-check-input" type="checkbox" checked={form.includeDeclined} onChange={(e) => setForm({ ...form, includeDeclined: e.target.checked })} /> <span className="form-check-label">Základ včetně odmítnutých transakcí (PaP stav Approved + Declined)</span></label></div>}
              </div>
            </div>
            <div className="card-footer prototype-modal-actions">
              <button type="button" className="btn prototype-secondary-btn" disabled={busy} onClick={() => setForm(null)}>Zrušit</button>
              <button type="button" className="btn btn-primary prototype-primary-btn" disabled={busy} onClick={save}>{busy ? 'Ukládám…' : 'Uložit'}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function KlientKontaktySection({ id }: { id: number }) {
  const { me } = useAuth();
  const canManage = !!me?.permissionKeys?.includes('affilmaster:manage');
  const [groupVersion, setGroupVersion] = useState(0);
  const [groups, setGroups] = useState<MailingGroupsValue | null>(null);
  const [origEmail, setOrigEmail] = useState('');
  const [rows, setRows] = useState<KlientContact[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [form, setForm] = useState<ContactForm | null>(null); // null = zavřeno
  const [editId, setEditId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getAffilmasterKlientContacts(id)
      .then((d) => { if (!cancelled) setRows(d); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof AffilmasterApiError ? e.message : 'Načtení kontaktů selhalo'); });
    return () => { cancelled = true; };
  }, [id, reload]);
  useFeather([rows, form]);
  function openAdd() { setEditId(null); setFormErr(null); setGroups(null); setOrigEmail(''); setForm({ ...EMPTY_CONTACT }); }
  function openEdit(c: KlientContact) { setEditId(c.id); setFormErr(null); setGroups(null); setOrigEmail(c.email ?? ''); setForm({ name: c.name, email: c.email ?? '', phone: c.phone ?? '', position: c.position ?? '', vocative: c.vocative ?? '', invoiceCc: !!c.invoiceCc }); }
  async function save() {
    if (!form) return;
    if (form.name.trim().length < 2) { setFormErr('Jméno musí mít aspoň 2 znaky'); return; }
    setBusy(true); setFormErr(null);
    try {
      const p = { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), position: form.position.trim(), vocative: form.vocative.trim(), invoiceCc: !!form.invoiceCc };
      if (editId == null) await addAffilmasterKlientContact(id, p);
      else await updateAffilmasterKlientContact(id, editId, p);
      await saveMailingGroups(origEmail, p.email, groups);
      setForm(null); setGroups(null); setGroupVersion((v) => v + 1); setReload((r) => r + 1);
    } catch (e) { setFormErr(e instanceof AffilmasterApiError ? e.message : 'Uložení selhalo'); }
    finally { setBusy(false); }
  }
  async function remove(c: KlientContact) {
    if (!(await confirmDialog({ title: 'Smazat kontakt?', message: `Kontakt „${c.name}“ se smaže.`, confirmLabel: 'Smazat', danger: true }))) return;
    try { await deleteAffilmasterKlientContact(id, c.id); setReload((r) => r + 1); }
    catch (e) { setError(e instanceof AffilmasterApiError ? e.message : 'Smazání selhalo'); }
  }
  if (error) return <div className="alert alert-danger mb-0">{error}</div>;
  if (!rows) return <div className="text-muted py-3"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>;
  return (
    <div className="prototype-contacts-view">
      <AffilmasterDetailSectionHeader title="Kontakty" description={`${rows.length} kontaktních osob klienta`} action={canManage && (
          <Button type="button" variant="secondary" size="sm" onClick={openAdd}><i data-feather="plus" /> Přidat osobu</Button>
        )} />
      {rows.length === 0 ? <div className="prototype-empty-contact">Zatím žádný kontakt.</div> : (
        <AffilmasterContactDirectory
          key={groupVersion}
          label="Kontaktní osoby klienta"
          contacts={rows.map((c) => ({
            id: c.id,
            name: c.name,
            role: `${c.position || 'Kontaktní osoba'}${c.invoiceCc ? ' · dostává faktury' : ''}`,
            vocative: c.vocative,
            email: c.email,
            phone: c.phone,
          }))}
          onEdit={canManage ? (contact: AffilmasterContactDirectoryItem) => {
            const source = rows.find((row) => row.id === contact.id);
            if (source) openEdit(source);
          } : undefined}
          onDelete={canManage ? (contact: AffilmasterContactDirectoryItem) => {
            const source = rows.find((row) => row.id === contact.id);
            if (source) void remove(source);
          } : undefined}
        />
      )}
      {form && (
        <div className="prototype-modal-backdrop" onClick={() => setForm(null)}>
          <div onClick={(e) => e.stopPropagation()} className="card prototype-modal-card" style={{ width: '100%', maxWidth: 520 }}>
            <div className="prototype-modal-head"><div><div className="prototype-eyebrow">KONTAKT</div><h2>{editId == null ? 'Přidat osobu' : 'Upravit osobu'}</h2></div><button type="button" className="prototype-modal-close" aria-label="Zavřít" onClick={() => setForm(null)}>×</button></div>
            <div className="card-body">
              {formErr && <div className="alert alert-danger py-2">{formErr}</div>}
              <div className="row g-2">
                <div className="col-md-7"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Jméno *</label>
                  <input className="form-control form-control-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="col-md-5"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Funkce</label>
                  <input className="form-control form-control-sm" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="např. account manager" /></div>
                <div className="col-md-7"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>E-mail</label>
                  <input type="email" className="form-control form-control-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="col-md-5"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Telefon</label>
                  <input className="form-control form-control-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div className="col-12"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Vokativ (5. pád)</label>
                  <input className="form-control form-control-sm" value={form.vocative} onChange={(e) => setForm({ ...form, vocative: e.target.value })} placeholder="např. Matěji, Veroniko, Jiří" /></div>
                <div className="col-12"><MailingGroupsField email={origEmail} currentEmail={form.email} value={groups} onChange={setGroups} /></div>
                <div className="col-12"><div className="form-check">
                  <input className="form-check-input" type="checkbox" id="klient-contact-invoice-cc" checked={!!form.invoiceCc} onChange={(e) => setForm({ ...form, invoiceCc: e.target.checked })} />
                  <label className="form-check-label" htmlFor="klient-contact-invoice-cc" style={{ fontSize: '0.82rem' }}>Posílat faktury tomuto kontaktu</label>
                </div></div>
              </div>
            </div>
            <div className="card-footer d-flex" style={{ gap: 8 }}>
              <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={save}>Uložit</button>
              <button type="button" className="btn btn-sm btn-link ms-auto" onClick={() => setForm(null)}>Zrušit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KlientAffiliatesSection({ id, currency, onOpenAffiliate }: { id: number; currency: string; onOpenAffiliate: (id: string) => void }) {
  const [rows, setRows] = useState<KlientAffiliateRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getAffilmasterKlientAffiliates(id)
      .then((d) => { if (!cancelled) setRows(d.affiliates); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof AffilmasterApiError ? e.message : 'Načtení affiliatů selhalo'); });
    return () => { cancelled = true; };
  }, [id]);
  useFeather([rows]);
  if (error) return <div className="alert alert-danger mb-0">{error}</div>;
  if (!rows) return <div className="text-muted py-3"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>;
  return <section><AffilmasterDetailSectionHeader title="Affiliates" description="Affiliate partneři přivádějící výkon klientovi" />
    {rows.length === 0 ? <div className="prototype-empty-contact">Žádní affiliates.</div> : <ArcPanel as="div" className="prototype-entity-list">
      {rows.map((a, index) => { const roi = kRoi(a.ngr_volume, a.commission, false); return <button type="button" className="prototype-entity-row" key={a.pap_affiliate_id} onClick={() => onOpenAffiliate(a.pap_affiliate_id)} aria-label={`Otevřít affiliate ${a.name}`}>
        <span className={`prototype-entity-mark tone-${index % 4}`}>{kInitials(a.name)}</span>
        <span className="prototype-entity-main"><strong>{a.name}</strong><small>{a.pap_affiliate_id} · {kInt(a.campaigns)} kampaní</small></span>
        <span className="prototype-entity-stats"><small>Reg. {kInt(a.registrations)} · FTD {kInt(a.ftd_count)} · Vklady {kMoney(a.deposits_volume, currency)} · NGR {kMoney(a.ngr_volume, currency)}</small><strong>RS {kMoney(a.commission_rs, currency)} · CPA {kMoney(a.commission_cpa, currency)} · ROI {roi ? `${roi} %` : '-'}</strong></span>
        <span className="prototype-entity-value">{kMoney(a.commission, currency)}</span>
        <span className="prototype-entity-arrow feather-icon" aria-hidden="true"><i data-feather="chevron-right" /></span>
      </button>; })}
    </ArcPanel>}
  </section>;
}

type KSum = { registrations: number; ftd_count: number; deposits_volume: number; ngr_volume: number; commission: number; withdrawals_count: number };
function kSum(months: KlientMonth[]): KSum {
  return months.reduce<KSum>((a, m) => ({
    registrations: a.registrations + m.registrations, ftd_count: a.ftd_count + m.ftd_count,
    deposits_volume: a.deposits_volume + m.deposits_volume, ngr_volume: a.ngr_volume + m.ngr_volume,
    commission: a.commission + m.commission, withdrawals_count: a.withdrawals_count + m.withdrawals_count,
  }), { registrations: 0, ftd_count: 0, deposits_volume: 0, ngr_volume: 0, commission: 0, withdrawals_count: 0 });
}

function KlientDokumentySection({ id }: { id: number }) {
  const { me } = useAuth();
  const canManage = !!me?.permissionKeys?.includes('affilmaster:manage');
  return (
    <KlientDokumenty
      canManage={canManage}
      load={() => getAffilmasterKlientDocuments(id)}
      refresh={() => refreshAffilmasterKlientDocuments(id)}
      saveFolder={(url) => setAffilmasterKlientDriveFolder(id, url)}
    />
  );
}

function KlientDetailView({ row, onBack, onOpenAffiliate, onOpenEsign }: { row: KlientListItem; onBack: () => void; onOpenAffiliate: (id: string) => void; onOpenEsign?: (documentId: number) => void }) {
  const [section, setSection] = useState<string>(() => peekKlientSectionHint() ?? 'prehled');
  useEffect(() => { clearKlientSectionHint(); }, []);
  const [detail, setDetail] = useState<KlientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<KDrillModal>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoNote, setLogoNote] = useState<string | null>(null);
  const [logoBg, setLogoBg] = useState<string | null>(null);
  const [editErr, setEditErr] = useState<string | null>(null);
  const [uolBusy, setUolBusy] = useState(false);
  const [uolMsg, setUolMsg] = useState<string | null>(null);
  const [papInput, setPapInput] = useState('');
  const [papBusy, setPapBusy] = useState(false);
  const [papPick, setPapPick] = useState<PapCampaignPick[] | null>(null);
  const [eForm, setEForm] = useState({ name: '', ic: '', vat: '', market: 'CZ', currency: 'CZK', website: '', platform: '', affiliatePlatform: '', exclusive: true, billingEmails: '' });
  const [platformSuggestions, setPlatformSuggestions] = useState<{ platforms: string[]; affiliatePlatforms: string[] }>({ platforms: [], affiliatePlatforms: [] });

  useEffect(() => {
    void loadKlientDeals(row.id, true).catch(() => undefined);
  }, [row.id]);

  useEffect(() => {
    let cancelled = false;
    getAffilmasterKlientDetail(row.id)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof AffilmasterApiError ? e.message : 'Detail selhal'); });
    return () => { cancelled = true; };
  }, [row.id, reloadKey]);

  function openEdit(d: KlientDetail) {
    const mkt = (d.market || d.uolCountry || 'CZ');
    setEForm({
      name: d.name || '', ic: d.ic || '', vat: d.vat || d.uolDic || '',
      market: ['CZ', 'SK', 'Global'].includes(mkt) ? mkt : 'CZ',
      currency: d.currency === 'EUR' ? 'EUR' : 'CZK',
      website: d.website || '', platform: d.platform || '', affiliatePlatform: d.affiliatePlatform || '', exclusive: !!d.isExclusive, billingEmails: d.billingEmails || '',
    });
    getCrmPlatformSuggestions().then(setPlatformSuggestions).catch(() => undefined);
    setEditErr(null); setUolMsg(null); setLogoBg(d?.logoBg ?? row.logoBg ?? null); setEditOpen(true);
  }
  async function saveEdit() {
    if (eForm.name.trim().length < 2) { setEditErr('Zobrazovaný název musí mít aspoň 2 znaky'); return; }
    setEditBusy(true); setEditErr(null);
    try {
      await updateAffilmasterPartner(row.id, {
        name: eForm.name, ic: eForm.ic || undefined, vat: eForm.vat || undefined,
        market: eForm.market, billingCurrency: eForm.currency, website: eForm.website || undefined, platform: eForm.platform, affiliatePlatform: eForm.affiliatePlatform, isExclusive: eForm.exclusive,
        billingEmails: eForm.billingEmails,
      });
      setEditOpen(false); setReloadKey((r) => r + 1);
    } catch (e) { setEditErr(e instanceof AffilmasterApiError ? e.message : 'Uložení selhalo'); }
    finally { setEditBusy(false); }
  }
  // Limit hlídá i server (2 MB), tady se ozveme dřív, ať uživatel nečeká na
  // odeslání souboru, který stejně neprojde.
  async function uploadLogo(file: File) {
    const MAX = 2 * 1024 * 1024;
    if (file.size > MAX) {
      setEditErr(`Logo má ${(file.size / 1024 / 1024).toFixed(1)} MB, maximum jsou 2 MB.`);
      return;
    }
    setLogoBusy(true); setEditErr(null);
    setLogoNote(`Nahrávám ${file.name} (${Math.round(file.size / 1024)} kB)…`);
    try {
      const payload = logoBg ? await composeLogoOnBackground(file, logoBg) : { dataUrl: await readFileAsDataUrl(file), mime: file.type };
      await uploadAffilmasterLogo(row.id, payload.dataUrl, payload.mime, logoBg);
      setLogoNote('Logo uloženo.');
      setReloadKey((r) => r + 1);
      window.setTimeout(() => setLogoNote(null), 2500);
    } catch (e) {
      setLogoNote(null);
      setEditErr(e instanceof AffilmasterApiError ? e.message : 'Nahrání loga selhalo');
    }
    finally { setLogoBusy(false); }
  }
  async function checkUol() {
    setUolBusy(true); setUolMsg(null); setEditErr(null);
    try {
      const r = await uolCheckAffilmasterKlient(row.id);
      setUolMsg(r.matched ? `✓ Napojeno: ${r.name ?? '-'}${r.dic ? ` (${r.dic})` : ''}` : 'Nenalezeno v UOL podle IČ.');
      setReloadKey((k) => k + 1);
    } catch (e) { setEditErr(e instanceof AffilmasterApiError ? e.message : 'Ověření UOL selhalo'); }
    finally { setUolBusy(false); }
  }

  useEffect(() => {
    if (!editOpen || papPick) return;
    let cancelled = false;
    getAffilmasterPapCampaigns()
      .then((list) => { if (!cancelled) setPapPick(list); })
      .catch(() => { if (!cancelled) setPapPick([]); });
    return () => { cancelled = true; };
  }, [editOpen, papPick]);

  async function addPapCampaign() {
    const id = papInput.trim();
    if (!/^[\w-]{1,64}$/.test(id)) { setEditErr('PaP ID kampaně smí obsahovat jen písmena, číslice, pomlčku a podtržítko.'); return; }
    setPapBusy(true); setEditErr(null);
    try {
      const known = papPick?.find((c) => c.id === id);
      await pairAffilmasterCampaign(row.id, id, known?.name ?? null);
      setPapInput('');
      setReloadKey((k) => k + 1);
    } catch (e) { setEditErr(e instanceof AffilmasterApiError ? e.message : 'Napárování kampaně selhalo'); }
    finally { setPapBusy(false); }
  }
  async function removePapCampaign(rowId: number, label: string) {
    if (!(await confirmDialog({ title: 'Odebrat kampaň od klienta?', message: `Historická data zůstanou, kampaň „${label}“ se jen přestane klientovi počítat.`, confirmLabel: 'Odebrat', danger: true }))) return;
    setPapBusy(true); setEditErr(null);
    try {
      await unpairAffilmasterCampaign(row.id, rowId);
      setReloadKey((k) => k + 1);
    } catch (e) { setEditErr(e instanceof AffilmasterApiError ? e.message : 'Odebrání kampaně selhalo'); }
    finally { setPapBusy(false); }
  }

  useFeather([detail, section, editOpen, editBusy, logoBusy, uolBusy, papBusy]);

  const d = detail;
  const name = d?.name ?? row.name;
  const company = d?.uolCompanyName ?? d?.company ?? row.company;
  const currency = d?.currency ?? row.currency;
  const market = d?.market ?? row.market;
  const logo = d?.logo ?? row.logo;
  const [vipView, setVipView] = useTabParam<'vse' | 'am' | 'vip'>('vip', KLIENT_VIP_VIEWS, 'vse', false);
  const hasVipCampaigns = !!d?.monthsByView;
  const months = !d ? [] : vipView === 'am' && d.monthsByView ? d.monthsByView.affilmaster : vipView === 'vip' && d.monthsByView ? d.monthsByView.vip : d.months;
  const visibleCampaigns = (d?.campaignList ?? []).filter((c) => vipView === 'vse' || !hasVipCampaigns || (vipView === 'vip' ? !!c.isVip : !c.isVip));
  const vipSwitch = hasVipCampaigns ? (
    <div className="aff-view-switch" role="radiogroup" aria-label="Zdroj výkonu">
      {KLIENT_VIP_VIEWS.map((v) => (
        <button key={v} type="button" role="radio" aria-checked={vipView === v} className={vipView === v ? 'active' : ''} onClick={() => setVipView(v)}>{KLIENT_VIP_LABEL[v]}</button>
      ))}
    </div>
  ) : null;
  const totals = kSum(months);
  const web = d?.website ?? row.website;
  const webHost = web ? web.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '') : null;
  const isExclusive = d?.isExclusive ?? row.isExclusive;
  const curSym = currency === 'EUR' ? '€' : 'Kč';
  const allDays = (d?.campaignList ?? []).flatMap((c) => [c.firstDay, c.lastDay]).filter(Boolean) as string[];
  const activeFrom = allDays.length ? kLongDate(allDays.reduce((a, b) => (a < b ? a : b))) : null;
  const activeTo = allDays.length ? kLongDate(allDays.reduce((a, b) => (a > b ? a : b))) : null;
  const ngrRoi = totals.commission ? Math.round(((totals.ngr_volume - totals.commission) / totals.commission) * 100) : null;

  return (
    <ArcPage as="div" className="klient-detail-view klient-detail-arc">
      <div className="klient-detail-back-row"><Button type="button" variant="secondary" size="sm" onClick={onBack}>Zpět na seznam</Button></div>

      {error && <div className="alert alert-danger">{error}</div>}

      <ArcPanel className="klient-detail-hero">
          <div className="klient-detail-hero-row">
            <div className="klient-detail-identity">
              {logo
                ? <img className="klient-detail-avatar" src={logo} alt={name} {...logoFrameAttrs(d?.logoBg ?? row.logoBg)} />
                : <div className="klient-detail-avatar klient-detail-initials">{kInitials(name)}</div>}
              <div className="klient-detail-identity-copy">
                <div className="klient-detail-eyebrow">Klient</div>
                <h1 className="klient-detail-name">{name}</h1>
                {company && company !== name && <p className="klient-detail-company">{company}</p>}
                <div className="klient-detail-meta"><span>#{d?.id ?? row.id}</span><i>•</i><span>{MARKET_FLAG[market]?.[1] ?? (market === 'FOREIGN' ? 'Global' : market)}</span>{isExclusive && <span className="prototype-status is-active">Exkluzivní</span>}</div>
              </div>
            </div>
            <div className="klient-detail-actions">
              <Button type="button" size="sm" disabled={!d} onClick={() => d && openEdit(d)}>Upravit</Button>
            </div>
          </div>
      </ArcPanel>

      <ArcTabs label="Karty klienta" items={KSECTIONS.map((s) => {
          const cnt = s.id === 'kampane' ? (d?.campaignList.length ?? 0) : s.id === 'affiliates' ? (d?.affiliatesCount ?? 0) : s.id === 'mesice' ? months.length : null;
          return { value: s.id, label: <>{s.label}{cnt != null && cnt > 0 && <span className="klient-detail-tab-count">{cnt}</span>}</> };
        })} value={section} onChange={setSection} className="klient-detail-tabs" />

      <ArcPanel className="klient-detail-content">
          {!d && !ROW_ONLY_SECTIONS.has(section) && <div className="prototype-loading" aria-label="Načítám"><span /><span /><span /><span /></div>}

          {d && section === 'prehled' && (
            <>
              <AffilmasterDetailMetricSection title={months[0] ? `Aktuální měsíc · ${kMonthLabel(months[0].ym)}` : 'Aktuální měsíc'} row={months[0] ?? null} currency={currency} />
              <AffilmasterDetailMetricSection title="Celkem" row={totals} currency={currency} />
              <NgrRoiCard value={ngrRoi} currency={currency} />
              <DetailPerformanceChart months={months} />
            </>
          )}

          {d && section === 'udaje' && (
            <div className="aff-detail-fields-layout">
              <AffilmasterDetailFieldGroup title="Identifikace">
                <AffilmasterDetailField label="Název firmy" value={d.uolCompanyName ?? d.company ?? d.name} />
                <AffilmasterDetailField label="Zobrazovaný název" value={d.name} />
                <AffilmasterDetailField label="ID klienta" code={String(d.id)} />
                <AffilmasterDetailField label="PaP ID kampaně" value={d.campaignList.filter((c) => c.active).length === 0
                  ? null
                  : <span className="udaje-item-codes">{d.campaignList.filter((c) => c.active).map((c) => <code key={c.papCampaignId} title={c.campaignName ?? undefined}>{c.papCampaignId}</code>)}</span>} />
                <AffilmasterDetailField label="IČO" value={d.ic} />
                <AffilmasterDetailField label="DIČ" value={d.vat ?? d.uolDic} />
                <AffilmasterDetailField label="Země" value={d.market ?? d.uolCountry} />
              </AffilmasterDetailFieldGroup>
              <AffilmasterDetailFieldGroup title="Fakturace a banka">
                <AffilmasterDetailField label="Měna fakturace" value={d.currency} />
                <AffilmasterDetailField label="Fakturační e-maily" value={d.billingEmails || null} />
                <AffilmasterDetailField label="Exkluzivní" value={d.isExclusive ? 'Ano' : 'Ne'} />
              </AffilmasterDetailFieldGroup>
              <AffilmasterDetailFieldGroup title="Platformy">
                <AffilmasterDetailField label="Kasino platforma" value={d.platform} />
                <AffilmasterDetailField label="Affiliate platforma" value={d.affiliatePlatform} />
              </AffilmasterDetailFieldGroup>
              <AffilmasterDetailFieldGroup title="Weby">
                <AffilmasterDetailField label="Hlavní web" value={web ? <WebStacked url={web} /> : null} />
              </AffilmasterDetailFieldGroup>
            </div>
          )}

          {d && section === 'mesice' && (
            <section>
              <AffilmasterDetailSectionHeader title="Měsíční rozpad" description={`Výkon klienta po jednotlivých měsících${vipView === 'am' ? ', jen Affilmaster síť' : vipView === 'vip' ? ', jen VIP partneři z instance kasina' : ''}`} action={vipSwitch} />
              <ArcTableFrame className="aff-table-wrap" label="Měsíční rozpad klienta">
              <div className="table-responsive-mobile">
              <table className="table table-sm align-middle mb-0 aff-detail-table aff-breakdown-table">
                <thead><tr>
                  <th>Měsíc</th><th className="text-end">Aff.</th><th className="text-end">Proklik</th>
                  <th className="text-end">Reg.</th><th className="text-end">FTD</th><th className="text-end">Vklady (#)</th>
                  <th className="text-end">Vklady</th><th className="text-end">NGR</th><th className="text-end">NGR ROI</th>
                  <th className="text-end">Odm. RS</th><th className="text-end">Odm. CPA</th>
                  <th className="text-end">AM RS</th><th className="text-end">AM CPA</th><th className="text-end pe-2" />
                </tr></thead>
                <tbody>
                  {months.length === 0 && <tr><td colSpan={14} className="text-center text-muted py-3">Žádná data.</td></tr>}
                  {months.map((m) => {
                    const ftdPct = m.registrations ? kPct1((m.ftd_count / m.registrations) * 100) : null;
                    const roi = kRoi(m.ngr_volume, m.commission, true);
                    const roiNum = m.commission ? Math.round(((m.ngr_volume - m.commission) / m.commission) * 100) : null;
                    return (
                      <tr key={m.ym}>
                        <td><strong>{m.ym.slice(5, 7)}/{m.ym.slice(0, 4)}</strong></td>
                        <td className="text-end">{kInt(m.affiliates)}</td>
                        <td className="text-end">{m.clicks ? kInt(m.clicks) : <span className="text-muted">-</span>}</td>
                        <td className="text-end">{kInt(m.registrations)}</td>
                        <td className="text-end">{kInt(m.ftd_count)}{ftdPct && <span className="aff-cell-pct">({ftdPct} %)</span>}</td>
                        <td className="text-end">{kInt(m.deposits_count)}</td>
                        <td className="text-end">{kMoney(m.deposits_volume, currency)}</td>
                        <td className="text-end">{kMoney(m.ngr_volume, currency)}</td>
                        <td className="text-end" style={roiNum != null ? { color: roiColorHex(roiNum), fontWeight: 600 } : undefined}>{roi ? `${roi} %` : <span className="text-muted">-</span>}</td>
                        <td className="text-end">{kMoney(m.commission_rs, currency)}</td>
                        <td className="text-end">{kMoney(m.commission_cpa, currency)}</td>
                        <td className="text-end text-muted">-</td>
                        <td className="text-end text-muted">-</td>
                        <td className="text-end pe-2"><RowArrow title="Detail měsíce" onClick={() => setModal({ kind: 'month', ym: m.ym })} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
              </ArcTableFrame>
            </section>
          )}

          {d && section === 'kampane' && (
            <section>
              <AffilmasterDetailSectionHeader title="Kampaně" description={`Výkon klienta podle kampaní${vipView === 'am' ? ', jen Affilmaster síť' : vipView === 'vip' ? ', jen VIP partneři z instance kasina' : ''}`} action={vipSwitch} />
              {visibleCampaigns.length === 0 ? <div className="prototype-empty-contact">Žádné kampaně.</div> : <ArcPanel as="div" className="prototype-entity-list">
                {visibleCampaigns.map((c, index) => {
                  const roi = kRoi(c.ngr_volume, c.commission, false);
                  const cname = c.campaignName ?? c.papCampaignId;
                  return <button type="button" className="prototype-entity-row" key={c.papCampaignId} onClick={() => setModal({ kind: 'campaign', cid: c.papCampaignId, cname })}>
                    <span className={`prototype-entity-mark tone-${index % 4}`}>{kInitials(cname).slice(0, 1)}</span>
                    <span className="prototype-entity-main"><strong>{cname}{c.isVip && <VipBadge />}</strong><small>{c.isVip ? c.papCampaignId.split(':').pop() : c.papCampaignId} · {kPeriod(c.firstDay, c.lastDay)}</small></span>
                    <span className="prototype-entity-stats"><small>Reg. {kInt(c.registrations)} · FTD {kInt(c.ftd_count)} · Vklady {kMoney(c.deposits_volume, currency)} · NGR {kMoney(c.ngr_volume, currency)}</small><strong>RS {kMoney(c.commission_rs, currency)} · CPA {kMoney(c.commission_cpa, currency)} · ROI {roi ? `${roi} %` : '-'}</strong></span>
                    <span className="prototype-status is-active">Aktivní</span><span className="prototype-entity-value">{kMoney(c.commission, currency)}</span><span className="prototype-entity-arrow feather-icon" aria-hidden="true"><i data-feather="chevron-right" /></span>
                  </button>;
                })}
              </ArcPanel>}
            </section>
          )}

          {d && section === 'affiliates' && <KlientAffiliatesSection id={row.id} currency={currency} onOpenAffiliate={onOpenAffiliate} />}

          {section === 'dealy' && <KlientDealySection id={row.id} defaultCurrency={currency === 'EUR' ? 'EUR' : 'CZK'} vipBilling={d?.vipBilling ?? false} vipInstances={d?.vipInstances ?? []} onVipBillingChanged={() => setReloadKey((r) => r + 1)} />}
          {section === 'kontakty' && <KlientKontaktySection id={row.id} />}
          {section === 'poznamky' && <KlientPoznamkySection id={row.id} />}
          {section === 'doklady' && <KlientDokladySection id={row.id} />}
          {section === 'smlouvy' && <KlientSmlouvySection id={row.id} onOpenDocument={onOpenEsign} />}
          {section === 'dokumenty' && <KlientDokumentySection id={row.id} />}
          {section === 'promo-news' && <KlientPromoNews partnerId={row.id} onOpen={(id) => window.location.assign(`${window.location.hostname.split('.')[0] === 'affilmaster' ? '' : '/affilmaster'}/vystupy/promo-news/${id}`)} />}
          {section === 'vystupy' && <KlientVystupy partnerId={row.id} />}
          {section === 'helpdesk' && <KlientHelpdeskSection id={row.id} />}
      </ArcPanel>

      {modal?.kind === 'campaign' && (
        <CampaignChartModal
          campaignName={modal.cname}
          fetchChart={() => getAffilmasterKlientCampaignChart(row.id, modal.cid)}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'month' && (
        <KlientMonthModal id={row.id} ym={modal.ym} onClose={() => setModal(null)} />
      )}

      {editOpen && (
        <div className="prototype-modal-backdrop aff-editor-backdrop" onClick={() => !(editBusy || logoBusy || uolBusy) && setEditOpen(false)}>
          <form
            className="aff-editor-modal aff-editor-modal-client"
            role="dialog"
            aria-modal="true"
            aria-busy={editBusy || logoBusy || uolBusy}
            aria-labelledby="aff-client-editor-title"
            aria-describedby="aff-client-editor-description"
            onSubmit={(e) => { e.preventDefault(); void saveEdit(); }}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="aff-editor-head">
              <div>
                <div className="aff-editor-kicker"><i data-feather="briefcase" /> Affilmaster · klient</div>
                <h2 id="aff-client-editor-title">Upravit klienta</h2>
                <p id="aff-client-editor-description">Firemní profil, fakturace a napojení účetnictví.</p>
              </div>
              <button type="button" className="aff-editor-close" aria-label="Zavřít" disabled={editBusy || logoBusy || uolBusy} onClick={() => setEditOpen(false)}><i data-feather="x" /></button>
            </header>

            <div className="aff-editor-body">
              {editErr && <div className="aff-editor-alert" role="alert"><i data-feather="alert-circle" /><span>{editErr}</span></div>}

              <div className="aff-editor-layout">
                <section className="aff-editor-main" aria-labelledby="aff-client-company-heading">
                  <div className="aff-editor-section-head">
                    <h3 id="aff-client-company-heading">Firemní údaje</h3>
                    <p>Údaje používané v přehledech a vyúčtování.</p>
                  </div>

                  <div className="aff-editor-grid">
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Zobrazovaný název</span>
                      <input className="aff-editor-control" autoFocus value={eForm.name} onChange={(e) => setEForm({ ...eForm, name: e.target.value })} />
                    </label>
                    <label className="aff-editor-field">
                      <span>IČO</span>
                      <input className="aff-editor-control" inputMode="numeric" value={eForm.ic} onChange={(e) => setEForm({ ...eForm, ic: e.target.value })} />
                    </label>
                    <label className="aff-editor-field">
                      <span>DIČ</span>
                      <input className="aff-editor-control" value={eForm.vat} onChange={(e) => setEForm({ ...eForm, vat: e.target.value })} />
                    </label>
                    <label className="aff-editor-field">
                      <span>Trh</span>
                      <select className="aff-editor-control" value={eForm.market} onChange={(e) => setEForm({ ...eForm, market: e.target.value })}><option>CZ</option><option>SK</option><option>Global</option></select>
                    </label>
                    <label className="aff-editor-field">
                      <span>Měna fakturace</span>
                      <select className="aff-editor-control" value={eForm.currency} onChange={(e) => setEForm({ ...eForm, currency: e.target.value })}><option>CZK</option><option>EUR</option></select>
                    </label>
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Hlavní web</span>
                      <div className="aff-editor-input-icon"><i data-feather="globe" /><input className="aff-editor-control" value={eForm.website} onChange={(e) => setEForm({ ...eForm, website: e.target.value })} placeholder="www.example.com" /></div>
                    </label>
                    <label className="aff-editor-field">
                      <span>Kasino platforma</span>
                      <input className="aff-editor-control" value={eForm.platform} onChange={(e) => setEForm({ ...eForm, platform: e.target.value })} placeholder="např. SoftSwiss, EveryMatrix, vlastní" list="aff-client-platform-options" />
                      <datalist id="aff-client-platform-options">{platformSuggestions.platforms.map((p) => <option key={p} value={p} />)}</datalist>
                    </label>
                    <label className="aff-editor-field">
                      <span>Affiliate platforma</span>
                      <input className="aff-editor-control" value={eForm.affiliatePlatform} onChange={(e) => setEForm({ ...eForm, affiliatePlatform: e.target.value })} placeholder="např. Post Affiliate Pro, Scaleo, žádná" list="aff-client-affiliate-platform-options" />
                      <datalist id="aff-client-affiliate-platform-options">{platformSuggestions.affiliatePlatforms.map((p) => <option key={p} value={p} />)}</datalist>
                    </label>
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Fakturační e-maily</span>
                      <div className="aff-editor-input-icon"><i data-feather="mail" /><input className="aff-editor-control" value={eForm.billingEmails} onChange={(e) => setEForm({ ...eForm, billingEmails: e.target.value })} placeholder="fakturace@klient.cz, dalsi@klient.cz" /></div>
                    </label>
                  </div>

                  <label className="aff-editor-toggle">
                    <input type="checkbox" checked={eForm.exclusive} onChange={(e) => setEForm({ ...eForm, exclusive: e.target.checked })} />
                    <span className="aff-editor-toggle-track" aria-hidden="true"><span /></span>
                    <span className="aff-editor-toggle-copy"><strong>Exkluzivní klient</strong><small>Klient je vedený jako výhradní partner Affilmasteru.</small></span>
                  </label>
                </section>

                <aside className="aff-editor-aside">
                  <section className="aff-editor-side-block" aria-labelledby="aff-client-logo-heading">
                    <div className="aff-editor-side-title"><div><h3 id="aff-client-logo-heading">Logo</h3><p>PNG, JPG, WebP nebo SVG</p></div></div>
                    <div className="aff-editor-logo-row">
                      <div className={`aff-editor-logo-preview ${d?.logoBg ? 'has-bg' : ''}`} style={logoFrameStyle(d?.logoBg)}>
                        {d?.logo ? <img src={d.logo} alt={`Logo ${eForm.name || name}`} /> : <span>{kInitials(eForm.name || name)}</span>}
                      </div>
                      <label className={`aff-editor-upload ${logoBusy || editBusy ? 'is-disabled' : ''}`}>
                        {/* Feather ikonu <i> nahradí za <svg> mimo React. Kdyby React ten
                            uzel odstraňoval, spadne na removeChild - proto se místo
                            záměny prvku jen přepíná viditelnost stále stejných uzlů. */}
                        <span className="aff-editor-upload-icon" aria-hidden="true">
                          <span className="aff-editor-spinner" style={{ display: logoBusy ? undefined : 'none' }} />
                          <span style={{ display: logoBusy ? 'none' : undefined }}><i data-feather="upload-cloud" /></span>
                        </span>
                        {' '}{logoBusy ? 'Nahrávám…' : 'Změnit logo'}
                        <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden disabled={logoBusy || editBusy}
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadLogo(f); e.currentTarget.value = ''; }} />
                      </label>
                      {logoNote && <small className="aff-editor-help aff-logo-note">{logoNote}</small>}
                    </div>
                    <KlientLogoBgPicker value={logoBg} onChange={setLogoBg} disabled={logoBusy || editBusy} />
                  </section>

                  <section className="aff-editor-side-block aff-editor-pap" aria-labelledby="aff-client-pap-heading">
                    <div className="aff-editor-side-title">
                      <div><h3 id="aff-client-pap-heading">PaP kampaně</h3><p>ID kampaně z Post Affiliate Pro</p></div>
                    </div>
                    {(d?.campaignList ?? []).filter((c) => c.active).length === 0 ? (
                      <p className="aff-editor-empty-copy">Klient zatím nemá napárovanou žádnou kampaň.</p>
                    ) : (
                      <ul className="aff-editor-pap-list">
                        {(d?.campaignList ?? []).filter((c) => c.active).map((c) => (
                          <li key={c.rowId}>
                            <span><strong>{c.campaignName ?? 'Kampaň bez názvu'}</strong><code>{c.papCampaignId}</code></span>
                            <button type="button" aria-label={`Odebrat kampaň ${c.papCampaignId}`} title="Odebrat kampaň" disabled={papBusy || editBusy} onClick={() => void removePapCampaign(c.rowId, c.campaignName ?? c.papCampaignId)}>×</button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="aff-editor-pap-add">
                      <input
                        className="aff-editor-control"
                        list="aff-client-pap-options"
                        value={papInput}
                        placeholder="PaP ID kampaně, např. 0c7d4dce"
                        aria-label="PaP ID kampaně"
                        disabled={papBusy || editBusy}
                        onChange={(e) => setPapInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void addPapCampaign(); } }}
                      />
                      <datalist id="aff-client-pap-options">
                        {(papPick ?? []).map((c) => <option key={c.id} value={c.id}>{c.name ?? c.id}</option>)}
                      </datalist>
                      <button type="button" className="aff-editor-button is-secondary" disabled={papBusy || editBusy || papInput.trim() === ''} onClick={() => void addPapCampaign()}>{papBusy ? 'Ukládám…' : 'Přidat'}</button>
                    </div>
                    <small className="aff-editor-help">Jedna kampaň může patřit jen jednomu aktivnímu klientovi. ID lze zadat i dřív, než se kampaň propíše z PaP.</small>
                  </section>

                  <section className="aff-editor-side-block aff-editor-uol" aria-labelledby="aff-client-uol-heading">
                    <div className="aff-editor-side-title">
                      <div><h3 id="aff-client-uol-heading">UOL účetnictví</h3><p>Párování podle IČO nebo DIČ</p></div>
                      <span className={`aff-editor-status ${d?.uolContactId ? 'is-connected' : 'is-neutral'}`}><span />{d?.uolContactId ? 'Napojeno' : 'Nenapojeno'}</span>
                    </div>

                    {d?.uolContactId ? (
                      <dl className="aff-editor-meta">
                        <div><dt>Firma</dt><dd>{d.uolCompanyName ?? '-'}</dd></div>
                        <div><dt>DIČ / země</dt><dd>{[d.uolDic, d.uolCountry].filter(Boolean).join(' · ') || '-'}</dd></div>
                        <div><dt>Kontakt ID</dt><dd><code>{d.uolContactId}</code></dd></div>
                        <div><dt>Ověřeno</dt><dd>{kDateTime(d.uolCheckedAt) ?? '-'}</dd></div>
                      </dl>
                    ) : <p className="aff-editor-empty-copy">Klient zatím není propojený s kontaktem v UOL.</p>}

                    {uolMsg && <div className="aff-editor-inline-message" role="status">{uolMsg}</div>}
                    <button type="button" className="aff-editor-uol-button" disabled={uolBusy || editBusy || logoBusy} onClick={() => void checkUol()}>
                      <span className="aff-editor-upload-icon" aria-hidden="true">
                        <span className="aff-editor-spinner" style={{ display: uolBusy ? undefined : 'none' }} />
                        <span style={{ display: uolBusy ? 'none' : undefined }}><i data-feather="refresh-cw" /></span>
                      </span>
                      {uolBusy ? 'Ověřuji…' : 'Ověřit napojení'}
                    </button>
                    <p className="aff-editor-footnote">Kontaktní osoby se spravují samostatně na tabu Kontakty.</p>
                  </section>
                </aside>
              </div>
            </div>

            <footer className="aff-editor-footer">
              <span>Změny se projeví v celém Affilmasteru.</span>
              <div>
                <button type="button" className="aff-editor-button is-secondary" disabled={editBusy || logoBusy || uolBusy} onClick={() => setEditOpen(false)}>Zrušit</button>
                <button type="submit" className="aff-editor-button is-primary" disabled={editBusy || logoBusy || uolBusy}>{editBusy ? <><span className="aff-editor-spinner" /> Ukládám…</> : 'Uložit změny'}</button>
              </div>
            </footer>
          </form>
        </div>
      )}
    </ArcPage>
  );
}
